'use client';

/**
 * McpWatchWindowCard — always-visible plan/room totals on the Insights rail.
 *
 * The Excel analogue of a watch window: a small, FIXED set of real
 * numbers (plan total, per-room-type totals — not user-configurable),
 * first in `mode1_costplan`'s `insights` list so it's visible ahead of
 * Plan Synthesis regardless of which canvas tab is open.
 *
 * Originally built pinned above Ask Klyde's own chat thread
 * (`ask-surface.tsx`) — wrong on two counts: that's the Messaging rail
 * peer, not Ask Klyde (a separate component entirely), and Ask Klyde
 * itself is a single hardcoded surface with no card-stacking mechanism
 * at all, so nothing could have mounted there as a real card regardless.
 * Insights is the actual matrix-driven card list this app has.
 *
 * Two modes, same card:
 *  - Idle (no compare active): one column, the plan's LIVE settled
 *    totals — today's default view.
 *  - Comparing (reads `useCostPlanCompareStore`, the same store the
 *    canvas diff card already writes to): two columns, one per side.
 *    "vs. current" always includes uncommitted WIP edits nobody's
 *    tagged, so a real comparison needs BOTH sides to be named,
 *    settled totals, not one live number unrelated to what's on
 *    screen. Room-by-room, not just the plan total — a line-by-line
 *    diff table doesn't fit a rail this narrow and isn't what this
 *    card is for; two settled totals per room type is.
 *
 * "Review changes" (visible only while comparing, and only when the
 * current preset actually ships an overlay) opens the Concertina —
 * mode1_costplan's `overlay_header_card`/`overlay_body_cards`
 * (obeya_costplan_review_header / obeya_costplan_review_groups), via
 * the SAME matrix-driven `openMatrix()` mechanism the product PDP
 * overlay already uses (see brief-card.tsx). Briefly removed from here
 * (moved to `obeya_costplan_versions`, reasoning: two buttons for one
 * action) and put back — real UX feedback: the canvas versions row
 * already grew to hold the pickers, so moving the button there gained
 * no vertical space at all, it just relocated the same line; Watch is
 * the one place this trigger is ALWAYS visible regardless of canvas
 * tab, which the versions row (scrolls with the canvas) isn't. The
 * versions row keeps its own pickers; this is the one real "go look"
 * action, and it lives here.
 *
 * This component is a pure renderer of `mcp_watch`'s response — it
 * does NOT union two room-label sets, cross-reference a second payload
 * for rev labels, or otherwise shape data itself. `room_comparison`
 * (pre-merged, one row per room type across both sides) and
 * `primary_label`/`secondary_label` (real published labels, resolved
 * server-side) all come pre-computed off the wire — see
 * `klyde_intelligence/projects/handler.py`, `mcp_watch`, and
 * `queries/costplan_pivot.py`, `merge_watch_room_totals`. BFF
 * marshals, this card paints (feedback_bff_discipline) — an earlier
 * draft of this card computed that merge client-side; moved server-
 * side on review, not a style preference.
 *
 * Self-fetching, parameterized by `mcpId` (`useViewportSelection`) and
 * the compare store — same pattern every other Insights/canvas card
 * here uses, not a frozen prop.
 *
 * All copy rides `mcp_watch`'s own `labels` (server-driven, see
 * `ui_config.handlers.obeya._obeya_labels`) — no hardcoded English.
 *
 * Chrome matches the sibling Insights cards' own convention (see e.g.
 * `snapshot-card.tsx`, `mcp-health-card.tsx` via `SynthesisCard`): an
 * outer `bg-card rounded-xl border` shell with its own header row
 * (icon + `text.heading` label), body in `text.muted`/`text.title`/
 * `text.value` — not ad hoc `text-xs` sizing. `CollapsibleCard`'s own
 * toggle strip (label + chevron) sits ABOVE this per-card box; the two
 * are separate chrome layers by design (see collapsible-card.tsx),
 * not a reason to skip this card's own box.
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — mcp_watch
 * @see klyde_edge/klyde_intelligence/projects/queries/costplan_pivot.py — merge_watch_room_totals
 * @see klyde_edge/ui_config/composition/cards/obeya.py — mcp_watch_window CardSpec
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-review-groups-card.tsx — opened by "Review changes"
 * @see klyde_ipe/lib/stores/costplan-compare-store.ts — shared compare state
 *
 * @component_role: Card
 * @component_type: Insights Card
 * @domain: Copilot / Insights (Cost Plan)
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Eye, GitCompare } from 'lucide-react';
import { text, status } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMcpWatch, type ApprovalFigures } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useTotalMatrixStore, useAffordance } from '@/lib/stores/total-matrix-store';

function money(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Approved · changes since · open — the approval record's three figures,
 *  painted by the BFF (approval_display.present_figures); shown once
 *  anything on the plan has been approved. */
function ApprovalFiguresRow({ figures }: { figures?: ApprovalFigures | null }) {
  if (!figures || figures.approved_count === 0) return null;
  const d = figures.display;
  return (
    <div className="flex flex-col gap-1 border-t border-border pt-2">
      <div className="grid grid-cols-3 gap-2">
        <div className="flex flex-col"><span className={text.muted}>{d.approved_label}</span><span className={cn(text.title, status.info, 'tabular-nums')}>{d.approved}</span></div>
        <div className="flex flex-col"><span className={text.muted}>{d.changes_label}</span><span className={cn(text.title, figures.changes_since === 0 ? '' : status.warn, 'tabular-nums')}>{d.changes_since}</span></div>
        <div className="flex flex-col"><span className={text.muted}>{d.open_label}</span><span className={cn(text.title, 'tabular-nums')}>{d.open}</span></div>
      </div>
      {d.non_price_changes && <span className={text.muted}>{d.non_price_changes}</span>}
      {d.plan_level_changes && <span className={text.muted}>{d.plan_level_changes}</span>}
    </div>
  );
}

export function McpWatchWindowCard() {
  const { mcpId, mode, contentSelection } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);

  // "Review changes" — opens the Concertina overlay (mode1_costplan's
  // overlay_header_card/overlay_body_cards) matrix-driven, same
  // openMatrix() mechanism the product PDP overlay already uses (see
  // brief-card.tsx). Only shown while comparing AND the current preset
  // actually ships an overlay — same hasMatrixOverlay guard, so this
  // button never renders as dead chrome on a preset with no Concertina.
  const openOverlayMatrix = useAgentCanvasStore((s) => s.openMatrix);
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const overlayViewport = getViewportConfig(mode, contentSelection);
  const hasMatrixOverlay =
    !!overlayViewport?.overlay_header_card || (overlayViewport?.overlay_body_cards?.length ?? 0) > 0;
  const reviewChangesTestId = useAffordance('canvas.watch_window.review_changes');

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  // Same stale-plan guard the diff card uses — a compare picked on a
  // plan the canvas has since navigated away from reads as "not
  // comparing" here too.
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;
  const isComparing = !!effectiveRevId;

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'watch'));
  const { data: raw, isLoading } = useMcpWatch(strategy, mcpId ?? null, effectiveRevId, effectiveAgainstRevId, lang ?? undefined);
  const watch = raw?.data;
  const L = watch?.labels ?? {};
  const primary = watch?.primary ?? null;
  const secondary = watch?.secondary ?? null;

  const queryKey = ['strategy-data', strategy, { semantic_id: mcpId ?? '', ...(effectiveRevId ? { rev_sid: effectiveRevId } : {}), ...(effectiveAgainstRevId ? { compare_rev_sid: effectiveAgainstRevId } : {}), ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'mcp_watch_window',
    status: (watch ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && strategy ? queryKey : null,
    text: primary ? `${L.watch_plan_total_label ?? ''}: ${money(primary.plan_total)}${secondary ? ` vs ${money(secondary.plan_total)}` : ''}` : '',
    json: watch ? { primary, secondary, room_comparison: watch.room_comparison } : null,
  }), [contentKey, watch, primary, secondary, mcpId, strategy, queryKey, L.watch_plan_total_label]));

  const hasSelections = !!primary && primary.plan_selected_count > 0;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Eye className="w-3.5 h-3.5 text-primary" />
        <span className={text.heading}>{L.watch_title ?? ''}</span>
      </div>
      <div className="p-4 space-y-2.5">
        {!mcpId ? (
          <span className={text.muted}>{L.select_cost_plan ?? ''}</span>
        ) : isLoading ? (
          <span className={text.muted}>…</span>
        ) : !hasSelections ? (
          <span className={text.muted}>{L.watch_no_selections ?? ''}</span>
        ) : !isComparing ? (
          // ── Idle: one column, live totals ──────────────────────────
          <>
            {/* Full text.value size (1.6em) — was overridden down to
                1.15em, which left this number reading at nearly the
                same weight as the room-type rows below it instead of
                standing out as THE figure on this card. */}
            <div className="flex items-baseline justify-between">
              <span className={text.muted}>{L.watch_plan_total_label ?? ''}</span>
              <span className={text.value}>{money(primary!.plan_total)}</span>
            </div>
            <ApprovalFiguresRow figures={primary!.approval_figures} />
            {primary!.room_totals.length > 0 && (
              <>
                <div className={cn(text.eyebrow, 'pt-1')}>{L.watch_rooms_sub ?? ''}</div>
                {primary!.room_totals.map((r) => (
                  <div key={r.label} className="flex items-baseline justify-between">
                    <span className={cn(text.muted, 'truncate')}>{r.label}</span>
                    <span className={cn(text.title, 'tabular-nums shrink-0 ml-2')}>{money(r.selected_total)}</span>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          // ── Comparing: two columns, both sides settled, room_comparison
          // already merged server-side (see mcp_watch's own docstring).
          // ONE grid for the whole block (header + plan total + room
          // rows), not three separate ones — a grid's column TRACKS are
          // only sized together within a single grid container, so
          // splitting this into per-section grids (an earlier draft did)
          // left the plan-total row's number columns free to land at
          // different widths than the room rows below it, nothing
          // actually lining up as a table despite each row being
          // internally right-aligned. `minmax(4.5em, auto)` number
          // columns, not a hard `4.5em` cap (real bug: plan_total is
          // qty-scaled now — costplan_pivot.py, build_plan_selected_total
          // — real totals run into 7+ digits, wider than 4.5em ever
          // allowed for; the fixed cap didn't clip the overflow, it let
          // both numbers render past their track with no visual gap,
          // reading as one concatenated string). `minmax` keeps the
          // single-grid alignment invariant (both number columns still
          // size from ONE shared width, the widest value across every
          // row) while actually fitting real numbers. ──
          <div className="grid grid-cols-[1fr_minmax(4.5em,auto)_minmax(4.5em,auto)] gap-x-2 gap-y-1.5 items-baseline">
            <span />
            <span className={cn(text.muted, 'font-semibold text-right truncate')} title={watch?.primary_label ?? ''}>{watch?.primary_label ?? ''}</span>
            <span className={cn(text.muted, 'font-semibold text-right truncate')} title={watch?.secondary_label ?? ''}>{watch?.secondary_label ?? ''}</span>

            <span className={text.muted}>{L.watch_plan_total_label ?? ''}</span>
            <span className={cn(text.title, 'tabular-nums text-right whitespace-nowrap')}>{money(primary!.plan_total)}</span>
            <span className={cn(text.title, 'tabular-nums text-right whitespace-nowrap')}>{secondary ? money(secondary.plan_total) : '—'}</span>

            {/* Each side's approval figures, worked out within that side —
                a Rev's never move after tagging. */}
            {((primary!.approval_figures?.approved_count ?? 0) > 0 || (secondary?.approval_figures?.approved_count ?? 0) > 0) && (
              (['approved', 'changes_since', 'open'] as const).map((k) => {
                const labelKey = k === 'approved' ? 'approved_label' : k === 'changes_since' ? 'changes_label' : 'open_label';
                const label = primary!.approval_figures?.display[labelKey] ?? secondary?.approval_figures?.display[labelKey] ?? '';
                return (
                  <div key={k} className="contents">
                    <span className={text.muted}>{label}</span>
                    <span className={cn(text.muted, 'tabular-nums text-right whitespace-nowrap')}>{primary!.approval_figures?.display[k] ?? '—'}</span>
                    <span className={cn(text.muted, 'tabular-nums text-right whitespace-nowrap')}>{secondary?.approval_figures?.display[k] ?? '—'}</span>
                  </div>
                );
              })
            )}
            {(primary!.check_code || secondary?.check_code) && (
              <>
                <span className={text.muted}>{L.rev_hash_label ?? ''}</span>
                <span className={cn(text.muted, 'font-mono text-right truncate')}>{primary!.check_code ?? '—'}</span>
                <span className={cn(text.muted, 'font-mono text-right truncate')}>{secondary?.check_code ?? '—'}</span>
              </>
            )}

            {(watch?.room_comparison?.length ?? 0) > 0 && (
              <>
                <div className={cn(text.eyebrow, 'col-span-3 pt-1')}>{L.watch_rooms_sub ?? ''}</div>
                {(watch?.room_comparison ?? []).map((row) => (
                  <div key={row.label} className="contents">
                    <span className={cn(text.muted, 'truncate')}>{row.label}</span>
                    <span className={cn(text.muted, 'tabular-nums text-right whitespace-nowrap')}>{money(row.primary_total)}</span>
                    <span className={cn(text.muted, 'tabular-nums text-right whitespace-nowrap')}>{money(row.secondary_total)}</span>
                  </div>
                ))}
              </>
            )}
            {hasMatrixOverlay && (
              <button
                data-testid={reviewChangesTestId}
                type="button"
                onClick={() => openOverlayMatrix()}
                className={cn(
                  'col-span-3 mt-1 flex items-center justify-center gap-1.5 rounded-md border border-border',
                  'py-1.5 text-primary hover:bg-primary/5',
                  text.muted,
                )}
              >
                <GitCompare className="w-3 h-3" />
                {L.review_open_button ?? ''}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
